import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Settings2, Plus, Trash2, Save, ExternalLink, LogOut } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/apiClient";

export default function Settings() {
  const [links, setLinks] = useState([]);
  const [logoutUrl, setLogoutUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/settings");
      setLinks(res.data.custom_links || []);
      setLogoutUrl(res.data.logout_redirect_url || "");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load settings.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addRow = () => setLinks((l) => [...l, { label: "", url: "" }]);
  const updateRow = (i, k, v) => setLinks((l) => l.map((row, j) => (j === i ? { ...row, [k]: v } : row)));
  const removeRow = (i) => setLinks((l) => l.filter((_, j) => j !== i));

  const save = async () => {
    const clean = links.filter((l) => l.label.trim() && l.url.trim());
    setSaving(true);
    try {
      const res = await api.put("/settings", { custom_links: clean, logout_redirect_url: logoutUrl });
      setLinks(res.data.custom_links);
      setLogoutUrl(res.data.logout_redirect_url);
      toast.success("Settings saved.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-lg bg-primary/10 flex items-center justify-center">
          <Settings2 className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="font-display text-3xl font-extrabold">Settings</h1>
          <p className="text-muted-foreground">Configure portal-wide options. Changes apply to all users.</p>
        </div>
      </div>

      <Card className="p-6" data-testid="custom-links-card">
        <div className="flex items-center gap-2 mb-1">
          <ExternalLink className="h-4 w-4 text-primary" />
          <h3 className="font-display font-bold">Header Link Buttons</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          These buttons appear in the top header bar for every logged-in user and open in the same tab.
        </p>

        {loading ? (
          <p className="text-muted-foreground text-sm">Loading...</p>
        ) : (
          <div className="space-y-3">
            {links.length === 0 && (
              <p className="text-muted-foreground text-sm">No custom link buttons yet.</p>
            )}
            {links.map((l, i) => (
              <div key={i} className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center" data-testid={`link-row-${i}`}>
                <Input
                  value={l.label}
                  onChange={(e) => updateRow(i, "label", e.target.value)}
                  placeholder="Button label (e.g. Support)"
                  className="sm:max-w-[220px]"
                  data-testid={`link-label-input-${i}`}
                />
                <Input
                  value={l.url}
                  onChange={(e) => updateRow(i, "url", e.target.value)}
                  placeholder="https://example.com or /path"
                  data-testid={`link-url-input-${i}`}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="text-red-600 hover:text-red-700 shrink-0 self-end sm:self-auto"
                  onClick={() => removeRow(i)}
                  data-testid={`delete-link-${i}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button type="button" variant="secondary" onClick={addRow} className="gap-2" data-testid="add-link-button">
              <Plus className="h-4 w-4" /> Add Link Button
            </Button>
          </div>
        )}
      </Card>

      <Card className="p-6" data-testid="logout-redirect-card">
        <div className="flex items-center gap-2 mb-1">
          <LogOut className="h-4 w-4 text-primary" />
          <h3 className="font-display font-bold">Logout Redirect URL</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          After signing out, users are sent to this URL instead of the login page. Leave empty to use the default login page.
        </p>
        <div className="space-y-1.5">
          <Label>Redirect URL</Label>
          <Input
            value={logoutUrl}
            onChange={(e) => setLogoutUrl(e.target.value)}
            placeholder="https://greenrgy.com or /login"
            data-testid="logout-redirect-input"
          />
        </div>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="gap-2" data-testid="save-settings-button">
          <Save className="h-4 w-4" /> {saving ? "Saving..." : "Save Settings"}
        </Button>
      </div>
    </div>
  );
}
